const MAX_API_ERROR_TITLE_LENGTH = 80;

/** Status text and the old universal title are not dealer-facing toast titles. */
const GENERIC_API_ERROR_TITLES = new Set([
  'warning',
  'warning!',
  'error',
  'erreur',
  'ok',
  'success',
  'bad request',
  'unauthorized',
  'payment required',
  'forbidden',
  'not found',
  'method not allowed',
  'not acceptable',
  'request timeout',
  'conflict',
  'gone',
  'internal server error',
  'not implemented',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  'unknown error',
  'http failure response',
]);

/** `title` or `Title` on an HTTP error body, before the toast decides it is usable. */
export function readApiErrorTitle(body: unknown): string | undefined {
  if (!isRecord(body)) {
    return undefined;
  }

  const camel = body['title'];
  const pascal = body['Title'];
  const raw = typeof camel === 'string' && camel.trim() ? camel : pascal;

  return typeof raw === 'string' ? raw : undefined;
}

/**
 * Title safe to show on an application-error toast.
 * Blank text, `Warning!`, HTTP status text, and a title that repeats the message are not used.
 */
export function readValidApiErrorTitle(value: unknown, message?: string): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const title = normalize(value);
  if (!title || title.length > MAX_API_ERROR_TITLE_LENGTH) {
    return undefined;
  }

  const normalized = title.toLowerCase();
  if (GENERIC_API_ERROR_TITLES.has(normalized) || normalized.startsWith('http failure response')) {
    return undefined;
  }

  if (/^\d{3}(?:\s|$)/.test(title)) {
    return undefined;
  }

  if (message && normalized === normalize(message).toLowerCase()) {
    return undefined;
  }

  return title;
}

function normalize(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
